import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { Alert } from 'react-bootstrap'

import useAppDispatch from '../../../../../hooks/useAppDispatch'
import useCurrentCompanyOrFail from '../../../../../hooks/useCurrentCompanyOrFail'
import useNotyf from '../../../../../hooks/useNotyf'
import { useRefreshSessionMetadataMutation } from '../../../../../hooks/useRefreshSessionMetadataMutation'
import companyService from '../../../../../lib/services/companyService'
import constraintViolationService from '../../../../../lib/services/constraintViolationService'
import errorTypeService from '../../../../../lib/services/errorTypeService'
import {
  endEdit,
  startEdit,
} from '../../../../../redux/slices/completeRegistrationPopup'
import UploadFile from '../../components/UploadFile/UploadFile'

interface Props {
  onUpdate?: () => void
}

const UploadLogo: React.FC<Props> = ({ onUpdate }) => {
  const dispatch = useAppDispatch()
  const currentCompany = useCurrentCompanyOrFail()
  const notyf = useNotyf()
  const refreshSessionMetadataMutation = useRefreshSessionMetadataMutation()

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) =>
      companyService.updateLogo(currentCompany.id, { logoFile: file }),
    onMutate: () => dispatch(startEdit()),
    onSuccess: () => {
      // Refresh the session metadata to ensure we have the latest company data
      refreshSessionMetadataMutation.mutate()

      if (onUpdate) {
        onUpdate()
      }
    },
    onError: (error) => {
      if (errorTypeService.isConstraintViolationListError(error)) {
        const formattedErrors = constraintViolationService.formatErrors(error)
        Object.values(formattedErrors).forEach((violation: string) => {
          notyf.errorWithoutReporting(violation)
        })
      }
    },
    onSettled: () => dispatch(endEdit()),
  })

  return (
    <div className="d-flex flex-column pop-in">
      <div className="d-flex flex-grow-1 flex-column align-items-stretch justify-content-center w-100">
        <Alert className="d-flex justify-content-center" variant="light">
          <div>
            <p className="mb-0 p-3 text-center">
              Please upload a high-resolution PNG copy of your firm's company
              logo with a transparent background.
            </p>
          </div>
        </Alert>

        <UploadFile
          uploadMutation={uploadLogoMutation}
          acceptableFileTypes={['image/png', 'image/jpeg', 'image/gif']}
          previewType="image"
          currentFileUrl={currentCompany.logoUrl}
          incorrectFileTypeMessage="Please choose a PNG image with a transparent background."
          currentFileExistsButIsUnavailable={false}
        />
      </div>
    </div>
  )
}

export default UploadLogo
